<template>
  <div class="scan">

    <!-- ── Top bar ─────────────────────────────────────────── -->
    <header class="scan__bar">
      <div class="scan__brand">
        <img src="@/assets/logo.png" alt="Event USA">
      </div>
      <div class="scan__barstatus">
        <span class="dot" :class="{'dot--live': $store.state.camera!=null}"></span>
        <span class="scan__barstatus-text">{{ $store.state.camera==null ? 'No camera' : (scanning ? 'Scanning' : 'Paused') }}</span>
      </div>
      <button
        class="eu-btn eu-btn--ghost scan__settings"
        @click="settingsModal = true"
        aria-label="Settings">
        <MDBIcon icon="cog" size="lg" />
        <span class="scan__settings-label">Settings</span>
      </button>
    </header>

    <!-- ── Main ────────────────────────────────────────────── -->
    <transition name="fade">
      <main class="scan__main" v-if="ready">

        <!-- Download failure -->
        <div class="scan__banner" v-if="downloadError" role="alert">
          <MDBIcon icon="exclamation-triangle" />
          <span>{{downloadError}}</span>
          <button class="scan__banner-x" @click="downloadError=''" aria-label="Dismiss">&times;</button>
        </div>

        <!-- Camera -->
        <section class="scan__camera">
          <div class="panel panel--camera" v-if="$store.state.camera==null">
            <button class="picker" @click="getCameras" aria-controls="cameraModal">
              <span class="picker__icon"><MDBIcon icon="camera-retro" size="3x" /></span>
              <span class="picker__title">Select a camera</span>
              <span class="picker__sub">Choose which camera to scan with</span>
            </button>
          </div>

          <div class="panel panel--camera" v-else>
            <div id="reader"></div>
            <p class="scan__hint" v-if="!cameraError">Hold the QR code steady inside the frame</p>
            <p class="scan__alert" v-else>{{cameraError}}</p>
          </div>
        </section>

        <!-- Result -->
        <section class="scan__result">
          <transition name="pop" mode="out-in">

            <!-- Empty state -->
            <div class="panel panel--empty" v-if="_.isEmpty(order)" key="empty">
              <MDBIcon icon="qrcode" size="4x" class="empty__icon" />
              <h2 class="empty__title">Ready to scan</h2>
              <p class="empty__sub">Order details will appear here once a ticket is scanned.</p>
            </div>

            <!-- Order card -->
            <article class="panel panel--order" :class="`is-${status}`" v-else key="order">

              <div class="status" :class="`status--${status}`">
                <MDBIcon :icon="status=='success' ? 'check-circle' : 'exclamation-triangle'" size="lg" />
                <span>{{ statusLabel }}</span>
                <span class="status__timer" :style="{animationDuration: autoClearMs+'ms'}"></span>
              </div>

              <header class="order__head" v-if="order.name||order.game_attending">
                <h2 class="order__name" v-if="order.name">{{order.name}}</h2>
                <p class="order__game" v-if="order.game_attending">{{order.game_attending}}</p>
              </header>

              <dl class="order__items" v-if="hasItems">
                <div class="order__row" v-if="order.adult_traditional_tailgate">
                  <dt>Adult Traditional</dt>
                  <dd class="tnum">{{order.adult_traditional_tailgate}}</dd>
                </div>
                <div class="order__row" v-if="order.adult_vip_tailgate">
                  <dt>Adult VIP</dt>
                  <dd class="tnum">{{order.adult_vip_tailgate}}</dd>
                </div>
                <div class="order__row" v-if="order.child_traditional_tailgate">
                  <dt>Child Traditional</dt>
                  <dd class="tnum">{{order.child_traditional_tailgate}}</dd>
                </div>
                <div class="order__row" v-if="order.child_vip_tailgate">
                  <dt>Child VIP</dt>
                  <dd class="tnum">{{order.child_vip_tailgate}}</dd>
                </div>
              </dl>

              <div class="order__members" v-if="order.tour_members">
                <h3 class="order__subhead">Tour Members</h3>
                <div class="order__memberlist" v-html="order.tour_members"></div>
              </div>

              <button class="eu-btn eu-btn--block eu-btn--lg order__clear" @click="clearOrder">
                Scan Next
              </button>
            </article>

          </transition>
        </section>

      </main>
    </transition>

    <!-- ── Camera modal ────────────────────────────────────── -->
    <MDBModal id="cameraModal" tabindex="-1" labelledby="cameraModalLabel" v-model="cameraModal">
      <MDBModalHeader>
        <MDBModalTitle id="cameraModalLabel">Select Camera</MDBModalTitle>
      </MDBModalHeader>
      <MDBModalBody>
        <MDBListGroup>
          <MDBListGroupItem
            @click="selectCamera(camera)"
            v-for="camera in $store.state.cameras"
            :key="camera.id"
            :active="$store.state.camera!=null&&camera.id == $store.state.camera.id">{{camera.label}}</MDBListGroupItem>
        </MDBListGroup>
      </MDBModalBody>
    </MDBModal>

    <!-- ── Settings modal ──────────────────────────────────── -->
    <MDBModal id="settingsModal" tabindex="-1" labelledby="settingsModalLabel" v-model="settingsModal">
      <MDBModalHeader>
        <MDBModalTitle id="settingsModalLabel">Settings</MDBModalTitle>
      </MDBModalHeader>
      <MDBModalBody>
        <div class="settings__actions">
          <button class="eu-btn eu-btn--block eu-btn--secondary" @click="getCameras(), this.settingsModal=false">
            <MDBIcon icon="camera" /> Select Camera
          </button>
          <button class="eu-btn eu-btn--block eu-btn--secondary" @click="settingsModal=false; ordersModal=true; downloadError=''; pagination.page = 0; $root.db.orders.clear(); $root.db.redeemed.clear(); counts={adult_traditional:0,adult_vip:0,child_traditional:0,child_vip:0}; getOrders()">
            <MDBIcon icon="cloud-download-alt" /> Download Orders
          </button>
        </div>

        <h3 class="settings__head">Redemption Counts</h3>
        <dl class="counts">
          <div class="counts__row">
            <dt>Adult Traditional</dt>
            <dd class="tnum">{{counts.adult_traditional}}</dd>
          </div>
          <div class="counts__row">
            <dt>Adult VIP</dt>
            <dd class="tnum">{{counts.adult_vip}}</dd>
          </div>
          <div class="counts__row">
            <dt>Child Traditional</dt>
            <dd class="tnum">{{counts.child_traditional}}</dd>
          </div>
          <div class="counts__row">
            <dt>Child VIP</dt>
            <dd class="tnum">{{counts.child_vip}}</dd>
          </div>
        </dl>

        <p class="settings__version">Version {{appVersion}}</p>
      </MDBModalBody>
    </MDBModal>

    <!-- ── Download progress modal ─────────────────────────── -->
    <MDBModal id="ordersModal" tabindex="-1" labelledby="ordersModalLabel" v-model="ordersModal" staticBackdrop>
      <MDBModalBody>
        <div class="download">
          <h3 class="download__title">Downloading Orders</h3>
          <p class="download__pct tnum">{{ Math.round(((pagination.page-1) / pagination.max_pages) * 100) }}%</p>
          <MDBProgress :height="10">
            <MDBProgressBar :value="(((pagination.page-1) / pagination.max_pages) * 100)" striped animated />
          </MDBProgress>
          <p class="download__sub">Keep this screen open until the download finishes.</p>
        </div>
      </MDBModalBody>
    </MDBModal>

  </div>
</template>

<script>
 import {MDBProgress, MDBProgressBar , MDBListGroup, MDBListGroupItem, MDBIcon, MDBTable , MDBSpinner, MDBInput , MDBCol, MDBRow, MDBContainer, MDBCard, MDBCardBody, MDBCardTitle, MDBCardText, MDBBtn,MDBModal, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBModalFooter} from 'mdb-vue-ui-kit';
 import { ref } from 'vue';
export default {
  name: 'Scan',
  components: {
    MDBProgress, MDBProgressBar , MDBListGroup, MDBListGroupItem, MDBIcon, MDBTable, MDBSpinner, MDBInput, MDBCol, MDBRow, MDBContainer, MDBCard, MDBCardBody, MDBCardTitle, MDBCardText, MDBBtn,MDBModal, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBModalFooter
  },
  data(){
    return {
        _:_,
      error:'',
      busy:false,
      ready:false,
      html5QrCode:null,
      localStorage:localStorage,
      scanning:false,
      pagination:{
        page:0,
        per_page:100,
        max_pages:20
      },
      order:{},
      // Auto-clear: how long a result stays up before the scanner resets itself.
      autoClearSuccessMs:5000,
      autoClearErrorMs:10000,
      autoClearTimer:null,
      autoClearMs:0,
      cameraError:'',
      downloadError:'',
      appVersion: process.env.VUE_APP_VERSION || 'dev',
      counts:{
        adult_traditional:0,
        adult_vip:0,
        child_traditional:0,
        child_vip:0
      },
    }
  },
  computed:{
    // Purely presentational: derived from the same error/order state the
    // scan handler already sets on #app as .success / .error
    status(){
      if(_.isEmpty(this.order)) return null;
      return _.isEmpty(this.error) ? 'success' : 'error';
    },
    statusLabel(){
      if(this.status == 'success') return 'Redeemed';
      return this.error;
    },
    hasItems(){
      return !!(this.order.adult_traditional_tailgate
        || this.order.adult_vip_tailgate
        || this.order.child_traditional_tailgate
        || this.order.child_vip_tailgate);
    }
  },
  watch:{
    // Start/cancel the auto-clear countdown whenever a result appears or goes away.
    order(val){
      this.stopAutoClear();
      if(_.isEmpty(val)) return;
      this.startAutoClear(this.status=='error' ? this.autoClearErrorMs : this.autoClearSuccessMs);
    },
    settingsModal:async function(newVal){
      if(!newVal) return;
      this.counts={
        adult_traditional:0,
        adult_vip:0,
        child_traditional:0,
        child_vip:0
      };
      let redeemed = await this.$root.db.redeemed.toArray();
      let orders = await this.$root.db.orders.toArray();

      for(let i = 0; i < redeemed.length; i++){
        let o = redeemed[i];
        let adult_traditional_tailgate = (o.meta_data.filter(m=>m.key=='adult_traditional_tailgate')[0]['value']);
        let adult_vip_tailgate = (o.meta_data.filter(m=>m.key=='adult_vip_tailgate')[0]['value']);
        let child_traditional_tailgate = (o.meta_data.filter(m=>m.key=='child_traditional_tailgate')[0]['value']);
        let child_vip_tailgate = (o.meta_data.filter(m=>m.key=='child_vip_tailgate')[0]['value']);
        if(adult_traditional_tailgate=='') adult_traditional_tailgate = 0;
        else adult_traditional_tailgate = parseInt(adult_traditional_tailgate);

        if(adult_vip_tailgate=='') adult_vip_tailgate = 0;
        else adult_vip_tailgate = parseInt(adult_vip_tailgate);
        
        if(child_traditional_tailgate=='') child_traditional_tailgate = 0;
        else child_traditional_tailgate = parseInt(child_traditional_tailgate);
        
        if(child_vip_tailgate=='') child_vip_tailgate = 0;
        else child_vip_tailgate = parseInt(child_vip_tailgate);

        this.counts['adult_traditional'] += adult_traditional_tailgate;
        this.counts['adult_vip'] += adult_vip_tailgate;
        this.counts['child_traditional'] += child_traditional_tailgate;
        this.counts['child_vip'] += child_vip_tailgate;
      }
    }
  },
  setup() {
      const cameraModal = ref(false);
      const settingsModal = ref(false);
      const ordersModal = ref(false);
      return {
        cameraModal,
        settingsModal,
        ordersModal
      };
    },
  async mounted(){
    if(this.$root.communicationChannel instanceof BroadcastChannel){

    }
    else{
      
    }
    if(localStorage.getItem('counts')==null) localStorage.setItem('counts', JSON.stringify({adult_traditional_tailgate:0,adult_vip_tailgate:0,child_traditional_tailgate:0,child_vip_tailgate:0}))
    this.$store.state.counts = JSON.parse(localStorage.getItem('counts'));
    this.ready = true;
    if(this.$route.path!='/'&&_.isEmpty(localStorage.getItem('token'))){ 
        this.$router.push('/'); 
    }
    else{
      await Sleep(150);
      
      this.scanning = true;
      if(!_.isEmpty(this.$store.state.camera)) this.startCamera();
    }
  },
  beforeUnmount(){
    this.stopAutoClear();
  },
  methods:{
    stopAutoClear(){
      if(this.autoClearTimer!=null){
        clearTimeout(this.autoClearTimer);
        this.autoClearTimer = null;
      }
    },
    startAutoClear(ms){
      this.stopAutoClear();
      this.autoClearMs = ms;
      this.autoClearTimer = setTimeout(()=>{
        this.autoClearTimer = null;
        this.clearOrder();
      }, ms);
    },
    async clearOrder(){
      this.stopAutoClear();
      this.order = {};
      this.scanning = true;
      this.error = '';
      document.querySelector('#app').classList.remove('success')
      document.querySelector('#app').classList.remove('error')
    },
    async getOrders(){
      this.pagination.page++;
      try{
        let req = await fetch(`https://eventusa.com/wp-json/wc/v3/orders?page=${this.pagination.page}&per_page=${this.pagination.per_page}`, {
          method:'GET',
          headers:{
            Authorization:`Basic ${btoa(`fpadmin:${localStorage.getItem('token')}`)}`
          }
        });
        let res = await req.json();

        // A non-array means the API returned an error body rather than orders.
        if(!Array.isArray(res)) throw new Error('Unexpected response while downloading orders');

        if(res.length > 0) await this.$root.db.orders.bulkAdd(res);

        // Keep going only while pages come back full; a short page is the last one.
        if(res.length == this.pagination.per_page && this.pagination.page < this.pagination.max_pages){
          return this.getOrders();
        }
      }
      catch(err){
        console.error('order download failed', err);
        this.downloadError = 'Order download failed. Check your connection and try again from Settings.';
      }

      this.ordersModal = false;
      this.pagination = {
        page:0,
        per_page:100,
        max_pages:20
      }
    },
    async openSettings(){},
    async getCameras(e){
      this.$store.state.cameras = await Html5Qrcode.getCameras();
      localStorage.setItem('cameras', JSON.stringify(this.$store.state.cameras));
      this.cameraModal = true;
    },
    async selectCamera(camera){
        if(this.$store.state.camera != null) await this.html5QrcodeScanner.stop();
        this.$store.state.camera = null;
        localStorage.setItem('camera', JSON.stringify(camera));
        this.$store.state.camera = camera;
        await Sleep(10);
        this.scanning = true;
        this.startCamera();
        
    }, 
    async startCamera(){
        this.html5QrcodeScanner=(new Html5QrcodeScanner("reader", {fps:10,qrbox:200}, false));
        let reader = document.querySelector('#reader');
        let size = 200;
       
        this.html5QrcodeScanner = new Html5Qrcode("reader");
        this.cameraError = '';
        this.html5QrcodeScanner.start(this.$store.state.camera.id, {fps:10, qrbox:200}, 
            async (decodedText, decodedResult)=>{
              if(this.scanning&&_.isEmpty(this.order)){
                this.scanning = false;

                // A code that isn't a well-formed order QR is reported the same
                // way as an order we can't find, so a bad scan is never silent.
                let response = null;
                try{ response = JSON.parse(decodedText); }
                catch(err){ response = null; }

                if(response==null||response.order_id==null){
                  this.order = {id:null};
                  document.querySelector('#app').classList.add('error');
                  this.error = 'Order does not exist on this device';
                  return;
                }

                let order =  (await this.$root.db.orders.where('id').equals(response.order_id).toArray());
                if(!_.isEmpty(order)){
                  order =  _.first(await this.$root.db.orders.where('id').equals(response.order_id).toArray());
                  this.order = {
                    id:order.id,
                    game_attending:_.filter(order.meta_data, m=>m.key=='game_attending')[0]['value'],
                    order_id:order.order_id,
                    name:order.line_items[0].name,
                    tour_members:'',
                    adult_traditional_tailgate:_.filter(order.meta_data, m=>m.key=='adult_traditional_tailgate')[0]['value'],
                    adult_vip_tailgate:_.filter(order.meta_data, m=>m.key=='adult_vip_tailgate')[0]['value'],
                    child_traditional_tailgate:_.filter(order.meta_data, m=>m.key=='child_traditional_tailgate')[0]['value'],
                    child_vip_tailgate:_.filter(order.meta_data, m=>m.key=='child_vip_tailgate')[0]['value'],
                    tailgate_redeemed:_.filter(order.meta_data, m=>m.key=='tailgate_redeemed')[0]['value'],
                  };
                  if(_.filter(order.line_items[0].meta_data, m=>m.key=='Tour Members').length > 0){
                    this.order.tour_members = _.filter(order.line_items[0].meta_data, m=>m.key=='Tour Members')[0]['value'];
                    this.order.tour_members = this.order.tour_members.split('&nbsp;&nbsp;&nbsp;&nbsp;&middot; ').filter(m=>{
                      return !_.isEmpty(m.trim());
                    }).join('<hr class="mt-0 mb-0">');
                  }
                  if(this.order.tailgate_redeemed!="1"){
                    document.querySelector('#app').classList.add('success');
                    
                    let id = order.order_id;
                    this.$root.registration.postMessage({...response, ...{appPassword: localStorage.getItem('appPassword'),token: localStorage.getItem('token')}});
                    
                    delete order.order_id;
                  }
                  else{
                    
                    document.querySelector('#app').classList.add('error');
                    this.error = 'Tailgate has already been redeemed';
                  }
                }
                else{
                  try{
                    let req = await fetch(`https://eventusa.com/wp-json/wc/v3/orders/${response.order_id}`, {
                      method:'GET',
                      headers:{
                        Authorization:`Basic ${btoa(`fpadmin:${localStorage.getItem('token')}`)}`
                      }
                    });
                    let res = await req.json();
                    order = res;
                    this.order = {
                      id:order.id,
                      game_attending:_.filter(order.meta_data, m=>m.key=='game_attending')[0]['value'],
                      order_id:order.order_id,
                      name:order.line_items[0].name,
                      tour_members:'',
                      adult_traditional_tailgate:_.filter(order.meta_data, m=>m.key=='adult_traditional_tailgate')[0]['value'],
                      adult_vip_tailgate:_.filter(order.meta_data, m=>m.key=='adult_vip_tailgate')[0]['value'],
                      child_traditional_tailgate:_.filter(order.meta_data, m=>m.key=='child_traditional_tailgate')[0]['value'],
                      child_vip_tailgate:_.filter(order.meta_data, m=>m.key=='child_vip_tailgate')[0]['value'],
                      tailgate_redeemed:_.filter(order.meta_data, m=>m.key=='tailgate_redeemed')[0]['value'],
                    };
                    if(_.filter(order.line_items[0].meta_data, m=>m.key=='Tour Members').length > 0){
                      this.order.tour_members = _.filter(order.line_items[0].meta_data, m=>m.key=='Tour Members')[0]['value'];
                      this.order.tour_members = this.order.tour_members.split('&nbsp;&nbsp;&nbsp;&nbsp;&middot; ').filter(m=>{
                        return !_.isEmpty(m.trim());
                      }).join('<hr class="mt-0 mb-0">');
                    }
                    // This order wasn't downloaded to the device. Cache it so the
                    // worker can find it, and so a re-scan sees it as redeemed.
                    // Best-effort: a failure here must not flip a valid scan to an error.
                    try{
                      if((await this.$root.db.orders.where('id').equals(order.id).toArray()).length==0){
                        await this.$root.db.orders.add(order);
                      }
                    }
                    catch(cacheErr){
                      console.error('could not cache order locally', cacheErr);
                    }

                    if(this.order.tailgate_redeemed!="1"){
                      document.querySelector('#app').classList.add('success');
                      
                      let id = order.order_id;
                      this.$root.registration.postMessage({...response, ...{appPassword: localStorage.getItem('appPassword'),token: localStorage.getItem('token')}});
                      
                      delete order.order_id;
                    }
                    else{
                      
                      document.querySelector('#app').classList.add('error');
                      this.error = 'Tailgate has already been redeemed';
                    }
                    
                  }
                  catch(err){
                    this.order= {id:response.order_id};
                    document.querySelector('#app').classList.add('error');
                    this.error = 'Order does not exist on this device';
                  }
                }

                
                
              }
            },
            (error)=>{
              // this.scanning = true;
            }
        ).catch(err=>{
            // Permission denied, camera busy, or no longer available.
            console.error('camera failed to start', err);
            this.scanning = false;
            this.cameraError = 'Could not start this camera. Check camera permissions, then pick a camera again.';
        });
    }
  }
}
</script>

<style lang="scss">
/* Not scoped: #reader's children are injected at runtime by html5-qrcode */

.scan{
  min-height:100vh;
  min-height:100dvh;
  display:flex;
  flex-direction:column;
}

/* ── Top bar ───────────────────────────────────────────── */
.scan__bar{
  position:sticky;
  top:0;
  z-index:20;
  display:flex;
  align-items:center;
  gap:1rem;
  height:var(--bar-height);
  padding:0 1rem;
  background:rgba(255,255,255,.85);
  backdrop-filter:blur(12px);
  border-bottom:1px solid var(--border);
}

.scan__brand img{
  height:34px;
  width:auto;
  display:block;
}

.scan__barstatus{
  display:flex;
  align-items:center;
  gap:.5rem;
  margin-right:auto;
  padding:.35rem .75rem;
  border:1px solid var(--border);
  border-radius:999px;
  background:var(--surface-sunken);
  font-size:.78rem;
  font-weight:600;
  letter-spacing:.04em;
  text-transform:uppercase;
  color:var(--text-muted);
}

.dot{
  width:8px;
  height:8px;
  border-radius:50%;
  background:var(--text-subtle);
  flex:none;

  &--live{
    background:var(--success);
    box-shadow:0 0 0 0 rgba(22,163,74,.5);
    animation:pulse 2s infinite;
  }
}

@keyframes pulse{
  0%{ box-shadow:0 0 0 0 rgba(22,163,74,.5); }
  70%{ box-shadow:0 0 0 7px rgba(22,163,74,0); }
  100%{ box-shadow:0 0 0 0 rgba(22,163,74,0); }
}

.scan__settings{
  min-height:44px;
  padding:0 1rem;
}

/* ── Layout ────────────────────────────────────────────── */
.scan__main{
  flex:1;
  width:100%;
  max-width:1180px;
  margin:0 auto;
  padding:1.5rem 1rem 2.5rem;
  display:grid;
  gap:1.5rem;
  align-content:start;
  align-items:start;

  /* Camera and result sit side by side at any width... */
  grid-template-columns:minmax(0,5fr) minmax(0,7fr);

  /* ...and only stack when the device is held in portrait. */
  @media (orientation:portrait){
    grid-template-columns:1fr;
  }

  @media (min-width:900px){
    padding:2rem 1.5rem 3rem;
    gap:2rem;
  }
}

.scan__alert{
  margin:.9rem 0 .25rem;
  text-align:center;
  font-size:.85rem;
  font-weight:600;
  line-height:1.45;
  color:var(--danger);
}

.scan__banner{
  grid-column:1 / -1;
  display:flex;
  align-items:center;
  gap:.7rem;
  padding:.9rem 1.1rem;
  border:1px solid var(--danger-border);
  border-radius:var(--r-md);
  background:var(--danger-soft);
  color:var(--danger);
  font-size:.9rem;
  font-weight:600;
}
.scan__banner-x{
  margin-left:auto;
  border:none;
  background:transparent;
  color:inherit;
  font-size:1.5rem;
  line-height:1;
  cursor:pointer;
  padding:0 .35rem;
}

/* Reserve the column so the out-in gap can't collapse it to zero height */
.scan__result{
  min-height:320px;
}

.panel{
  background:var(--surface);
  border:1px solid var(--border);
  border-radius:var(--r-lg);
  box-shadow:var(--shadow-md);
  padding:1.25rem;
}

/* ── Camera ────────────────────────────────────────────── */
.panel--camera{
  padding:1rem;
}

#reader{
  margin:0 auto;
  width:100% !important;
  border:none !important;
  border-radius:var(--r-md);
  overflow:hidden;
  background:#0f172a;
  display:flex;
  align-items:center;
  justify-content:center;

  video{
    display:block;
    width:100% !important;
    height:auto !important;
    border-radius:var(--r-md);
    transform:scaleX(-1);
  }
  img{ display:none; }
}

.scan__hint{
  margin:.9rem 0 .25rem;
  text-align:center;
  font-size:.82rem;
  color:var(--text-muted);
}

.picker{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:.65rem;
  width:100%;
  min-height:280px;
  padding:2rem 1.5rem;
  border:2px dashed var(--border-strong);
  border-radius:var(--r-md);
  background:var(--surface-sunken);
  color:var(--text);
  cursor:pointer;
  transition:border-color .16s ease, background-color .16s ease;

  &:hover{
    border-color:var(--focus);
    background:#f0f6ff;
  }
  &:focus-visible{
    outline:none;
    border-color:var(--focus);
    box-shadow:0 0 0 3px rgba(37,99,235,.25);
  }
}
.picker__icon{ color:var(--text-subtle); }
.picker__title{ font-size:1.05rem; font-weight:700; }
.picker__sub{ font-size:.85rem; color:var(--text-muted); }

/* ── Empty state ───────────────────────────────────────── */
.panel--empty{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  text-align:center;
  gap:.5rem;
  min-height:320px;
  padding:2.5rem 1.5rem;
  background:transparent;
  border-style:dashed;
  box-shadow:none;
}
.empty__icon{ color:var(--text-subtle); opacity:.45; margin-bottom:.5rem; }
.empty__title{ margin:0; font-size:1.15rem; font-weight:700; }
.empty__sub{ margin:0; max-width:34ch; font-size:.88rem; color:var(--text-muted); }

/* ── Order card ────────────────────────────────────────── */
.panel--order{
  padding:0;
  overflow:hidden;

  &.is-success{ border-color:var(--success-border); }
  &.is-error{ border-color:var(--danger-border); }
}

.status{
  display:flex;
  align-items:center;
  justify-content:center;
  gap:.6rem;
  padding:1.15rem 1.25rem;
  font-size:1.05rem;
  font-weight:800;
  letter-spacing:.06em;
  text-transform:uppercase;
  text-align:center;
  color:#fff;

  &--success{ background:var(--success); }
  &--error{ background:var(--danger); }
}

.status{
  position:relative;
  overflow:hidden;
}

/* Draining bar: shows the result is about to clear itself */
.status__timer{
  position:absolute;
  left:0;
  bottom:0;
  width:100%;
  height:4px;
  background:rgba(255,255,255,.6);
  transform-origin:left center;
  animation:drain linear forwards;
}

@keyframes drain{
  from{ transform:scaleX(1); }
  to{ transform:scaleX(0); }
}

@media (prefers-reduced-motion:reduce){
  /* The global reduce rule would snap this to empty instantly; hide it instead. */
  .status__timer{ display:none; }
}

.order__head{
  padding:1.5rem 1.5rem 1.25rem;
  border-bottom:1px solid var(--border);
}
.order__name{
  margin:0;
  font-size:1.45rem;
  font-weight:700;
  line-height:1.25;
  letter-spacing:-.02em;
}
.order__game{
  margin:.4rem 0 0;
  font-size:.92rem;
  color:var(--text-muted);
}

.order__items{
  margin:0;
  padding:.5rem 1.5rem;
}
.order__row{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:1rem;
  padding:.85rem 0;
  border-bottom:1px solid var(--border);

  &:last-child{ border-bottom:none; }

  dt{ font-size:.95rem; color:var(--text-muted); }
  dd{
    margin:0;
    min-width:2.5rem;
    padding:.2rem .7rem;
    border-radius:var(--r-sm);
    background:var(--surface-sunken);
    font-size:1.15rem;
    font-weight:700;
    text-align:center;
  }
}

.order__members{
  padding:1.25rem 1.5rem;
  border-top:1px solid var(--border);
  background:var(--surface-sunken);
}
.order__subhead{
  margin:0 0 .75rem;
  font-size:.72rem;
  font-weight:700;
  letter-spacing:.1em;
  text-transform:uppercase;
  color:var(--text-muted);
}
.order__memberlist{
  font-size:.95rem;
  line-height:1.7;

  hr{
    margin:.5rem 0;
    border:none;
    border-top:1px solid var(--border);
    opacity:1;
  }
}

.order__clear{
  border-radius:0;
  box-shadow:none;
}

/* ── Settings modal ────────────────────────────────────── */
.settings__actions{
  display:grid;
  gap:.75rem;
  margin-bottom:2rem;
}
.settings__head{
  margin:0 0 .75rem;
  font-size:.72rem;
  font-weight:700;
  letter-spacing:.1em;
  text-transform:uppercase;
  color:var(--text-muted);
}
.counts{
  margin:0;
  border:1px solid var(--border);
  border-radius:var(--r-md);
  overflow:hidden;
}
.settings__version{
  margin:1.25rem 0 0;
  text-align:center;
  font-size:.75rem;
  letter-spacing:.06em;
  text-transform:uppercase;
  color:var(--text-subtle);
}

.counts__row{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:1rem;
  padding:.8rem 1rem;
  border-bottom:1px solid var(--border);

  &:last-child{ border-bottom:none; }
  &:nth-child(odd){ background:var(--surface-sunken); }

  dt{ font-size:.9rem; color:var(--text-muted); }
  dd{ margin:0; font-size:1.05rem; font-weight:700; }
}

/* ── Download modal ────────────────────────────────────── */
.download{ text-align:center; padding:.5rem 0; }
.download__title{
  margin:0 0 1.25rem;
  font-size:1.1rem;
  font-weight:700;
}
.download__pct{
  margin:0 0 .75rem;
  font-size:2.5rem;
  font-weight:800;
  letter-spacing:-.03em;
}
.download__sub{
  margin:1rem 0 0;
  font-size:.82rem;
  color:var(--text-muted);
}

/* ── Small screens ─────────────────────────────────────── */
@media (max-width:560px){
  .scan__settings-label{ display:none; }
  .order__head{ padding:1.25rem; }
  .order__items{ padding:.5rem 1.25rem; }
  .order__name{ font-size:1.25rem; }
}

/* ── Phone held sideways: drop the reserved heights so the two
      columns still fit on one screen ──────────────────────── */
@media (orientation:landscape) and (max-height:600px){
  .scan__main{ padding:1rem .75rem 1.5rem; gap:1rem; }
  .scan__result{ min-height:0; }
  .picker{ min-height:0; padding:1.25rem 1rem; }
  .panel--empty{ min-height:0; padding:1.5rem 1rem; }
}
</style>
